import {
	IconArrowLeft,
	IconLayoutSidebarLeftCollapse,
	IconLayoutSidebarLeftExpand,
} from "@tabler/icons-react";
import { useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { Button, Divider, theme } from "antd";
import { useEffect } from "react";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import { useWorkspaces } from "@/common/artemis-operations/workspaces";
import { AccountNav } from "../../account/account-nav";
import { AdminNav } from "../../admin/admin-nav";
import { CustomIcon } from "../../base";
import { WorkspaceNav } from "../../workspaces/workspace-nav";
import WorkspaceSelector from "../../workspaces/workspace-selector";
import { SidebarIdentity } from "./sidebar-identity";

const LAST_WORKSPACE_KEY = "lastWorkspaceId";

export function SideNav({
	collapsed,
	onNavigate,
	onToggleCollapse,
	fullHeight = true,
}: {
	collapsed?: boolean;
	onNavigate?: () => void;
	/** Given only by the desktop sidebar, which then owns the back/collapse footer. */
	onToggleCollapse?: () => void;
	fullHeight?: boolean;
}) {
	const router = useRouter();
	const {
		token: { colorSplit },
	} = theme.useToken();
	const matches = useRouterState({ select: (s) => s.matches });
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const navigate = useNavigate();
	const orgSlug = useRouteOrgSlug();
	const { data: workspaces } = useWorkspaces();
	const hasMultipleWorkspaces = (workspaces?.length ?? 0) > 1;
	const routeWorkspaceId = matches
		.map((m) => (m.params as Record<string, string>).workspaceId)
		.find(Boolean);

	// The org-admin and account sections each swap the whole left menu for their own
	// nav (same sidebar, different items); everywhere else shows the workspace nav.
	const isAdminSection = /^\/[^/]+\/admin(\/|$)/.test(pathname);
	const isAccountSection =
		pathname === "/account" || pathname.startsWith("/account/");

	// Remember the last workspace so returning from the org-admin area restores the
	// workspace nav without needing a workspaceId in the current route.
	useEffect(() => {
		if (routeWorkspaceId)
			localStorage.setItem(LAST_WORKSPACE_KEY, routeWorkspaceId);
	}, [routeWorkspaceId]);
	const workspaceId =
		routeWorkspaceId ?? localStorage.getItem(LAST_WORKSPACE_KEY) ?? undefined;
	const inWorkspace = !isAdminSection && !isAccountSection && !!workspaceId;

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				// Fixed sidebar: 100% of the Sider, with the back/collapse footer as the last
				// flex child (antd's own trigger is off, so nothing overlays the last item).
				// Mobile drawer: fill the flex-column body (flex:1 + minHeight:0)
				// so the inner list scrolls instead of overflowing.
				height: fullHeight ? "100%" : undefined,
				flex: fullHeight ? undefined : 1,
				minHeight: fullHeight ? undefined : 0,
			}}
		>
			<div style={{ padding: "8px 8px 4px" }}>
				<SidebarIdentity block collapsed={collapsed} />
				{inWorkspace && hasMultipleWorkspaces && orgSlug && (
					<>
						<Divider size="small" />
						<WorkspaceSelector
							activeId={workspaceId}
							collapsed={collapsed}
							onChange={(ws) =>
								ws &&
								navigate({
									to: "/$orgSlug/workspaces/$workspaceId",
									params: { orgSlug, workspaceId: ws.id },
								})
							}
						/>
						<Divider size="small" />
					</>
				)}
			</div>

			{/* minHeight:0 lets this flex child shrink below content height so it actually scrolls. */}
			<div
				className="no-scrollbar"
				style={{ flex: 1, minHeight: 0, overflow: "auto", paddingTop: 4 }}
			>
				{isAccountSection ? (
					<AccountNav onNavigate={onNavigate} />
				) : isAdminSection ? (
					<AdminNav onNavigate={onNavigate} />
				) : (
					workspaceId && (
						<WorkspaceNav
							workspace={{ id: workspaceId, name: workspaceId } as any}
							workspaceId={workspaceId}
							collapsed={collapsed}
							onNavigate={onNavigate}
						/>
					)
				)}
			</div>

			{onToggleCollapse && (
				// Collapsed there is no room for two buttons side by side, so back stacks above.
				<div
					style={{
						display: "flex",
						flexDirection: collapsed ? "column" : "row",
						alignItems: "center",
						justifyContent: collapsed ? "center" : "space-between",
						gap: 4,
						padding: 8,
						borderTop: `1px solid ${colorSplit}`,
					}}
				>
					<Button
						type="text"
						aria-label="Back"
						title="Back"
						icon={<CustomIcon icon={IconArrowLeft} />}
						onClick={() => router.history.back()}
					/>
					<Button
						type="text"
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						icon={
							<CustomIcon
								icon={
									collapsed
										? IconLayoutSidebarLeftExpand
										: IconLayoutSidebarLeftCollapse
								}
							/>
						}
						onClick={onToggleCollapse}
					/>
				</div>
			)}
		</div>
	);
}
